import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useAgentsStore } from "../../../../../../../../../../store/agents.store";
import { getApiErrorMessage } from "../../../../../../../../../../../../utils/getApiErrorMessage";
import { downloadBlob, extractFileName } from "../../../../../../../../../../../../utils/blobDownload";
import { DownloadDownLineHierarchy, UpdateAgentDownLinesStatus } from "./hierarchy.services";

const DOWNLINE_HIERARCHY_FILE_NAME = "DownlineAgentHierarchy.xlsx";

export const useHierarchyActions = () => {
    const userId = useAgentsStore((state) => state.detail.activeUserId);
    const fetchAgentDetails = useAgentsStore((state) => state.fetchAgentDetails);

    const [isProcessing, setIsProcessing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [pendingActivate, setPendingActivate] = useState<boolean | null>(null);

    const handleActivateClick = useCallback(() => {
        setPendingActivate(true);
    }, []);

    const handleInactivateClick = useCallback(() => {
        setPendingActivate(false);
    }, []);

    const handleConfirmCancel = useCallback(() => {
        setPendingActivate(null);
    }, []);

    const handleConfirmSubmit = useCallback(async () => {
        if (userId == null || pendingActivate === null) return;

        const flagActivateDownlines = pendingActivate;
        setPendingActivate(null);
        setIsProcessing(true);

        try {
            const response = await UpdateAgentDownLinesStatus({ userId, flagActivateDownlines });

            if (response.flagStatus) {
                useAgentsStore.setState({ hasLoadedAgents: false });
                toast.success(
                    flagActivateDownlines
                        ? "Downline users activated successfully"
                        : "Downline users inactivated successfully"
                );
                await fetchAgentDetails(userId, { silent: true });
            } else {
                toast.error(response.error || "Failed to update downline status");
            }
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Failed to update downline status"));
        } finally {
            setIsProcessing(false);
        }
    }, [userId, pendingActivate, fetchAgentDetails]);

    const handleDownloadHierarchy = useCallback(async () => {
        if (userId == null) return;

        setIsDownloading(true);
        try {
            const response = await DownloadDownLineHierarchy({ userId });
            const rawContentType = response.headers["content-type"];
            const contentType =
                typeof rawContentType === "string"
                    ? rawContentType
                    : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const blob = new Blob([response.data], { type: contentType });
            const fileName = extractFileName(
                response.headers,
                DOWNLINE_HIERARCHY_FILE_NAME
            );
            downloadBlob(blob, fileName);
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Failed to download hierarchy"));
        } finally {
            setIsDownloading(false);
        }
    }, [userId]);

    return {
        isProcessing,
        isDownloading,
        pendingActivate,
        handleActivateClick,
        handleInactivateClick,
        handleConfirmCancel,
        handleConfirmSubmit,
        handleDownloadHierarchy,
    };
};
